import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const s = {
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', marginBottom: 24 },
  form: { background: '#fff', borderRadius: 10, padding: 24, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
  input: { padding: '10px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 },
  select: { padding: '10px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 },
  btn: { padding: '10px 24px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', background: '#f9fafb', borderBottom: '1px solid #eee' },
  td: { padding: '12px 16px', fontSize: 14, borderBottom: '1px solid #f0f0f0' },
  actionBtn: (color) => ({ padding: '4px 12px', background: color, color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }),
};

export default function UsersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '', role: 'RECRUITER' });

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users', user?.tenantId],
    queryFn: () => userApi.list(user?.tenantId).then(r => r.data),
    enabled: !!user?.tenantId,
  });

  const registerMutation = useMutation({
    mutationFn: (data) => userApi.register({ ...data, tenantId: user?.tenantId }),
    onSuccess: () => {
      qc.invalidateQueries(['users']);
      setForm({ email: '', password: '', firstName: '', lastName: '', role: 'RECRUITER' });
      toast.success('User created');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create user'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id) => userApi.deactivate(id),
    onSuccess: () => { qc.invalidateQueries(['users']); toast.success('User deactivated'); },
  });

  const activateMutation = useMutation({
    mutationFn: (id) => userApi.activate(id),
    onSuccess: () => { qc.invalidateQueries(['users']); toast.success('User activated'); },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    registerMutation.mutate(form);
  };

  return (
    <div>
      <h1 style={s.title}>Users</h1>

      <div style={s.form}>
        <h3 style={{ fontWeight: 600, marginBottom: 16 }}>Add User</h3>
        <form onSubmit={handleSubmit}>
          <div style={s.grid}>
            <input style={s.input} placeholder="First Name *" required
              value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
            <input style={s.input} placeholder="Last Name *" required
              value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
            <input style={s.input} type="email" placeholder="Email *" required
              value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            <input style={s.input} type="password" placeholder="Password *" required minLength={8}
              value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <select style={s.select} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
              <option value="RECRUITER">Recruiter</option>
              <option value="TENANT_ADMIN">Tenant Admin</option>
            </select>
            <button style={s.btn} type="submit" disabled={registerMutation.isPending}>
              {registerMutation.isPending ? 'Creating...' : '+ Add User'}
            </button>
          </div>
        </form>
      </div>

      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Name</th>
            <th style={s.th}>Email</th>
            <th style={s.th}>Role</th>
            <th style={s.th}>Status</th>
            <th style={s.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td style={{ ...s.td, textAlign: 'center' }} colSpan={5}>Loading...</td></tr>}
          {users.map(u => (
            <tr key={u.id}>
              <td style={s.td}>{u.firstName} {u.lastName}</td>
              <td style={s.td}>{u.email}</td>
              <td style={s.td}><span style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>{u.role}</span></td>
              <td style={s.td}>
                <span style={{ color: u.active ? '#10b981' : '#ef4444', fontWeight: 600, fontSize: 13 }}>
                  {u.active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td style={s.td}>
                {u.active
                  ? <button style={s.actionBtn('#ef4444')} onClick={() => deactivateMutation.mutate(u.id)}>Deactivate</button>
                  : <button style={s.actionBtn('#10b981')} onClick={() => activateMutation.mutate(u.id)}>Activate</button>
                }
              </td>
            </tr>
          ))}
          {!isLoading && users.length === 0 && (
            <tr><td style={{ ...s.td, textAlign: 'center', color: '#999' }} colSpan={5}>No users found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
